import React from 'react';
import { useIntl } from 'react-intl';
import CustomerDetails from '../CustomerDetails';
import { ICustomer } from '../../helpers/types';
import { getFullnameFromUser } from '../../helpers/auth';
import { ReactComponent as IconCustomer } from '../../media/images/icon-customer.svg';
import { claimsLink, logoutLink, profileLink, protectionLink, offersLink } from '../../helpers/navLinks';
import { StyledNavIconWrapper, StyledNavIcon } from '../../styled/StyledNavButton';
import { StyledDropdown, StyledDropdownToggle, StyledDropdownButton } from '../../styled/StyledBaseDropdown';
import { LocaleTransformer } from '../../helpers/types/url';
import DropdownItem from './DropdownItem';
import { StyledDropdownItemDivider, StyledDropdownMenu } from './styledCustomerDropdown';

interface ICustomerDropdownProps {
  customer: ICustomer;
  localeTransformer?: LocaleTransformer;
}

const CustomerDropdown = ({ customer, localeTransformer }: ICustomerDropdownProps): JSX.Element => {
  const intl = useIntl();
  const fullName = getFullnameFromUser(customer) || '';
  const compatibilityLocaleTransformer = localeTransformer || ((path: string) => `/${intl.locale}${path}`);

  return (
    <StyledDropdown>
      <StyledDropdownToggle
        // Override with existing button
        tag={StyledDropdownButton}
        buttonType="tertiary"
        data-test-id="CustomerDropdown-toggle"
      >
        <StyledNavIconWrapper>
          <StyledNavIcon as={IconCustomer} />
        </StyledNavIconWrapper>
        {fullName}
      </StyledDropdownToggle>

      <StyledDropdownMenu $rtl={intl.bidi} end={!intl.bidi} direction="down" data-test-id="CustomerDropdown-menu">
        <CustomerDetails customer={customer} />
        <StyledDropdownItemDivider />
        <DropdownItem
          {...protectionLink(intl)}
          href={compatibilityLocaleTransformer(protectionLink(intl).path)}
          data-test-id="dropdown-item-protection"
        />
        <DropdownItem
          {...profileLink(intl)}
          href={compatibilityLocaleTransformer(profileLink(intl).path)}
          data-test-id="dropdown-item-profile"
        />
        <DropdownItem
          {...claimsLink(intl)}
          href={compatibilityLocaleTransformer(claimsLink(intl).path)}
          data-test-id="dropdown-item-claims"
        />
        <DropdownItem
          {...offersLink(intl)}
          href={compatibilityLocaleTransformer(offersLink(intl).path)}
          data-test-id="dropdown-item-offers"
        />
        <StyledDropdownItemDivider />
        <DropdownItem
          {...logoutLink(intl)}
          href={compatibilityLocaleTransformer(logoutLink(intl).path)}
          data-test-id="dropdown-item-logout"
        />
      </StyledDropdownMenu>
    </StyledDropdown>
  );
};

export default CustomerDropdown;
